import type { Ga4TrafficSummary } from '@lensora/shared'
import { useQuery } from '@tanstack/react-query'
import { Link2 } from 'lucide-react'
import { Link } from 'react-router-dom'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { ChartTooltip } from '@/components/charts/ChartTooltip'
import { useActiveWebsite } from '@/hooks/use-active-website'
import { api } from '@/lib/api'

interface Ga4Status {
  configured: boolean
  connected: boolean
}

type Ga4DataResponse = { connected: false } | ({ connected: true } & Ga4TrafficSummary)

const PLATFORM_COLORS: Record<string, string> = {
  ChatGPT: '#10a37f',
  Claude: '#d97757',
  Gemini: '#4285f4',
  Perplexity: '#8b5cf6',
  Copilot: '#0ea5e9',
  'You.com': '#f97316',
  Phind: '#ec4899',
}
const FALLBACK_COLOR = '#64748b'

export function Ga4TrafficWidget() {
  const { websiteId } = useActiveWebsite()

  const { data: status } = useQuery({
    queryKey: ['ga4-status', websiteId],
    queryFn: () => api.get<Ga4Status>(`/api/ga4/status?website_id=${websiteId}`),
    enabled: !!websiteId,
  })

  const { data, isLoading } = useQuery({
    queryKey: ['ga4-data', websiteId],
    queryFn: () => api.get<Ga4DataResponse>(`/api/ga4/data?website_id=${websiteId}&days=30`),
    enabled: !!websiteId && !!status?.connected,
  })

  // Feature not rolled out to this account yet — stay silent rather than
  // cluttering the analytics page with an integration nobody can use.
  if (!status?.configured) return null

  if (!status.connected) {
    return (
      <div className="rounded-xl border border-border bg-surface p-6">
        <div className="mb-1 flex items-center gap-2">
          <Link2 className="h-4 w-4 text-text-muted" />
          <h2 className="text-sm font-semibold text-text">AI Referral Traffic</h2>
        </div>
        <p className="mt-2 text-xs text-text-muted">
          Connect Google Analytics to see how many visitors ChatGPT, Perplexity, and other AI assistants are
          actually sending to your site.
        </p>
        <Link
          to="/dashboard/settings"
          className="mt-3 inline-block text-xs font-medium text-brand-accent hover:underline"
        >
          Connect Google Analytics →
        </Link>
      </div>
    )
  }

  const traffic = data && data.connected ? data : null
  const aiShare =
    traffic && traffic.totalSessions > 0
      ? Math.round((traffic.totalAiSessions / traffic.totalSessions) * 100)
      : 0

  return (
    <div className="rounded-xl border border-border bg-surface p-6">
      <div className="mb-5 flex items-start justify-between">
        <div>
          <h2 className="text-sm font-semibold text-text">AI Referral Traffic</h2>
          <p className="mt-0.5 text-xs text-text-muted">Sessions from AI assistants, last 30 days</p>
        </div>
        {traffic && (
          <span className="text-sm font-bold text-brand-accent">
            {traffic.totalAiSessions} sessions ({aiShare}% of total)
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="h-[200px] animate-pulse rounded-lg bg-surface-overlay" />
      ) : !traffic || traffic.series.length === 0 ? (
        <p className="py-16 text-center text-xs text-text-muted">No AI referral sessions in the last 30 days</p>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={traffic.series} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(226,232,240,0.6)" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: FALLBACK_COLOR }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 10, fill: FALLBACK_COLOR }} tickLine={false} axisLine={false} />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(226,232,240,0.4)' }} />
              <Bar dataKey="sessions" fill="#6366f1" radius={[2, 2, 0, 0]} name="Sessions" />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-3 flex flex-wrap gap-3">
            {traffic.byPlatform.map((p) => (
              <div key={p.platform} className="flex items-center gap-1.5 text-xs">
                <span
                  className="h-2 w-2 rounded-full"
                  style={{ backgroundColor: PLATFORM_COLORS[p.platform] ?? FALLBACK_COLOR }}
                />
                <span className="text-text-muted">
                  {p.platform} <span className="font-semibold text-text">{p.sessions}</span>
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
